import React, { Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Components

import Form from '@lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { preventerTools } from '@utils/tools/preventerTools';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import states from '@constants/configs/states';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Disability Information' Page
 *
 */
const Page2_23 = props => {

    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams,
                thCommonParams: {
                    hasSomeoneBlindOrDisabled,
                    hasSomeoneWithLongTermCare
                }
            }
        },
        environment: {
            stateCode,
        },
        householdMembers,
        form,
        formName
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const onFieldChange_member = (...args) => {
        const [ newData, indexInArray, , [ name ] ] = args;

        const {
            blindOrDisabled,
            longTermCare
        } = newData;

        const propName = name === 'blindOrDisabled'
            ? 'blind'
            : 'longterm';

        const newState = { ...data };

        newState.taxHousehold.householdMember[indexInArray] =
            {
                ...newData,
                disabilityIndicator: !!(blindOrDisabled || longTermCare)
            };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newDate, , , [ name, value ] ] = args;

        const propName = name === 'hasSomeoneBlindOrDisabled'
            ? 'blind'
            : 'longterm';

        const hhmPropName = name === 'hasSomeoneBlindOrDisabled'
            ? 'blindOrDisabled'
            : 'longTermCare';

        const updateIndividualValues = hhm => {
            const {
                blindOrDisabled,
                longTermCare
            } = hhm;

            return {
                ...hhm,
                [hhmPropName ]: value === false ? false: null,
                disabilityIndicator: hhmPropName === 'blindOrDisabled' ? !!longTermCare : !!blindOrDisabled
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newDate,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName
            }
        );

        props.actions.changeData(syncedData);
    };

    const getHouseholdMembers = (householdMembers, indicator, commonIndicator) => {

        const combinedMembers = [];

        householdMembers.forEach((member, i) => {

            const {
                applicantGuid,
                applyingForCoverageIndicator,
                name
            } = member;

            combinedMembers.push(
                <Fragment key = { `${ applicantGuid }_${ indicator }` }>
                    {
                        applyingForCoverageIndicator &&

                        <Fragment>
                            <ul className = 'usa-list usa-list--unstyled' >
                                <li>
                                    <UniversalInput
                                        label = { {
                                            text: 'common.input.label.non_internationalized_text',
                                            labelTextReplacements: {
                                                text: normalizeNameOfPerson(name)
                                            }
                                        } }
                                        inputData = { {
                                            isInputRequired: false,
                                            type: INPUT_TYPES.CHECKBOX,
                                            name: indicator,
                                            classes: [ '' ],
                                            checked: member[indicator],
                                            value: member[indicator],
                                            currentData: cloneObject(member),
                                            fields: [],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_member
                                        } }
                                        indexInArray = { i }
                                        errorClasses = { [ 'margin-l-0' ] }
                                    />
                                </li>
                            </ul>
                        </Fragment>
                    }
                </Fragment>
            );
        });

        combinedMembers.push(
            <UniversalInput
                key = { commonIndicator }
                label = { {
                    text: 'ssap.input.label.none_of_above',
                    isLabelRequired: false
                } }
                inputData = { {
                    isInputRequired: false,
                    type: INPUT_TYPES.CHECKBOX,
                    name: commonIndicator,
                    checked: thCommonParams[commonIndicator] === false,
                    value: thCommonParams[commonIndicator] === false,
                    currentData: cloneObject(data),
                    fields: [ 'taxHousehold', 'thCommonParams' ],
                    reversedValue: true,
                    isInputStandalone: true
                } }
                inputActions = { {
                    onFieldChange: onFieldChange_none_of_above
                } }
            />
        );
        return combinedMembers;
    };

    const checkIfBlindQuestionAnswered = () => (
        householdMembers.some(hhm => hhm.blindOrDisabled)
        || hasSomeoneBlindOrDisabled === false
    );

    const checkIfLongTermCareQuestionAnswered = () => (
        householdMembers.some(hhm => hhm.longTermCare)
        || hasSomeoneWithLongTermCare === false
    );

    const checkTopLevelValidation = () => checkIfBlindQuestionAnswered() && checkIfLongTermCareQuestionAnswered();

    return (
        <Fragment>
            <Form
                { ...{
                    formName
                } }
                topLevelValidity = { checkTopLevelValidation() }
            >
                <div
                    className = 'fade-in'
                    id = 'fhMoreInfo'
                >
                    <div className = 'subsection' >
                        <InstructionalText
                            Component = {
                                <p
                                    data-instructions = {
                                        stateCode !== states.NEW_JERSEY
                                            ? 'instructional_text.disability_information'
                                            : ''
                                    }
                                >
                                    { 'ssap.family_household.disabilities.any_disability' }
                                </p>
                            }
                            required
                        />

                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !checkIfBlindQuestionAnswered() }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        {
                            getHouseholdMembers(
                                householdMembers,
                                'blindOrDisabled',
                                'hasSomeoneBlindOrDisabled'
                            )
                        }

                        <InstructionalText
                            Component = {
                                <p
                                    className = 'padding-t-20'
                                    data-instructions = {
                                        stateCode === states.NEW_JERSEY
                                            ? 'instructional_text.disability_information'
                                            : ''
                                    }
                                >
                                    { 'ssap.family_household.disabilities.daily_activities' }
                                </p>
                            }
                            required
                        />

                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !checkIfLongTermCareQuestionAnswered() }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0' ] }
                        />

                        {
                            getHouseholdMembers(
                                householdMembers,
                                'longTermCare',
                                'hasSomeoneWithLongTermCare'
                            )
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,
    form: state.ssap.form,
    environment: state.common.environment,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_23);
